import React from 'react'
import { Play, Clock } from 'lucide-react'

import { useTranslation } from '../../hooks/use-i18n'

interface ArenaGameCardProps {
  whitePlayer: string
  blackPlayer: string
  result: 'white' | 'black' | 'draw' | 'ongoing'
  date: string
  moves?: number
  onReplay?: () => void
  lightTheme?: boolean
  accentColor?: string
}

const resultDisplay: Record<string, { text: string }> = {
  white: { text: '1-0' },
  black: { text: '0-1' },
  draw: { text: '½-½' },
  ongoing: { text: '...' },
}

export const ArenaGameCard: React.FC<ArenaGameCardProps> = ({
  whitePlayer,
  blackPlayer,
  result,
  date,
  moves,
  onReplay,
  lightTheme = false,
  accentColor,
}) => {
  const { t } = useTranslation()
  const display = resultDisplay[result] || resultDisplay.ongoing
  const handleCardClick = (e: React.MouseEvent) => {
    if (result === 'ongoing' || !onReplay) return
    if ((e.target as HTMLElement).closest('button')) return // avoid double fire when clicking Replay button
    onReplay()
  }
  return (
    <div
      role={result !== 'ongoing' && onReplay ? 'button' : undefined}
      tabIndex={result !== 'ongoing' && onReplay ? 0 : undefined}
      onKeyDown={result !== 'ongoing' && onReplay ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onReplay!() } } : undefined}
      onClick={handleCardClick}
      className={`p-2.5 sm:p-4 rounded-lg transition-all duration-300 ${
        lightTheme ? 'bg-gray-50 hover:bg-gray-100 border border-gray-200' : 'bg-secondary/50 border border-border hover:bg-secondary'
      } ${result !== 'ongoing' && onReplay ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-center justify-between mb-2 sm:mb-3 gap-1 sm:gap-2">
        <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-1">
          <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-foreground border border-border flex-shrink-0" />
          <span className={`font-medium truncate text-xs sm:text-sm md:text-base ${result === 'white' ? 'text-primary' : ''}`}>
            {whitePlayer}
          </span>
        </div>
        <span className="font-bold text-xs sm:text-sm md:text-base flex-shrink-0 px-1" style={lightTheme && accentColor ? { color: accentColor } : undefined}>
          {display.text}
        </span>
        <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-1 justify-end">
          <span className={`font-medium truncate text-xs sm:text-sm md:text-base ${result === 'black' ? 'text-primary' : ''}`}>
            {blackPlayer}
          </span>
          <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-background border border-border flex-shrink-0" />
        </div>
      </div>
      <div className={`flex items-center justify-between text-[10px] sm:text-xs md:text-sm gap-1 ${lightTheme ? 'text-gray-500' : 'text-muted-foreground'}`}>
        <div className="flex items-center gap-1 sm:gap-2 min-w-0">
          <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
          <span className="truncate">{date}</span>
          {moves != null && <span className="hidden sm:inline">• {moves} {t('chessArena.moves')}</span>}
        </div>
        {result !== 'ongoing' && onReplay && (
          <button
            type="button"
            onClick={onReplay}
            className="h-6 sm:h-7 px-1.5 sm:px-2 text-[10px] sm:text-xs flex-shrink-0 rounded border border-border hover:bg-muted"
          >
            <Play className="w-2.5 h-2.5 sm:w-3 sm:h-3 inline mr-0.5 sm:mr-1" />
            <span className="hidden sm:inline">{t('chessArena.replay')}</span>
          </button>
        )}
      </div>
    </div>
  )
}
